import React, { useState } from 'react'
import {
  RadioGroup,
  InputButton,
  Checkbox,
  Button,
  InputText,
  DatePicker,
  Label,
  Select,
  cn
} from '@pcln/horizon'
import { Discount } from 'pcln-icons'
import {
  getGridContainerClass,
  getFlightTypeClass,
  getInputClass
} from './HorizonFlightForm.variants'
import {
  FLIGHT_TYPE_OPTIONS,
  CABIN_OPTIONS,
  PLACEHOLDERS,
  LABELS,
  DEFAULT_VALUES
} from './constants'
import { FLIGHT_TYPE_CODE, MULTI_DESTINATION } from '../constants'
import {
  type FlightType,
  addFlightToList,
  removeFlightFromList,
  updateFlightInList,
  canAddMoreFlights
} from './utils/flightManagement'
import {
  onCalendarChange,
  createRadioOptions,
  handleFlightTypeChange,
  createToggleHandler
} from './utils/formHandlers'

export default function FlightSearchForm() {
  const [flightType, setFlightType] = useState<string>(
    DEFAULT_VALUES.FLIGHT_TYPE
  )
  const [addHotel, setAddHotel] = useState<boolean>(DEFAULT_VALUES.ADD_HOTEL)
  const [addCar, setAddCar] = useState<boolean>(DEFAULT_VALUES.ADD_CAR)
  const [cabinClass, setCabinClass] = useState<string>(
    DEFAULT_VALUES.CABIN_CLASS
  )

  // State for managing multiple flights in multi-destination mode
  const [flights, setFlights] = useState<FlightType[]>([
    { id: '1', origin: '', destination: '', departureDate: undefined }
  ])

  // Determine if cabin class should be hidden
  const isPackage = addHotel || addCar

  const handleUpdateFlight = (
    flightId: string,
    field: keyof FlightType,
    value: string | Date | undefined
  ) => {
    setFlights(updateFlightInList(flights, flightId, field, value))
  }

  return (
    <div className="w-auto">
      <form
        className="w-auto"
        aria-label="horizon-flight-search-form"
        onSubmit={e => e.preventDefault()}
      >
        <div className="w-auto mx-auto">
          <div
            className={getGridContainerClass(flightType, addHotel, isPackage)}
          >
            {/* Flight Type Radio Buttons */}
            <div className={getFlightTypeClass(flightType)}>
              <RadioGroup
                className="flex-row gap-4"
                name="flightType"
                options={createRadioOptions()}
                defaultValue={FLIGHT_TYPE_OPTIONS[0].value}
                required
                aria-labelledby="flight-type"
                handleChange={(value: unknown) => {
                  handleFlightTypeChange(value, setFlightType)
                }}
              />
              {/* Cabin class dropdown - conditionally rendered */}
              {!isPackage && (
                <div className="lg:block hidden">
                  <Select
                    size="sm"
                    label={LABELS.CABIN_CLASS}
                    menuItems={[...CABIN_OPTIONS]}
                    initialValue={cabinClass}
                    onChange={setCabinClass}
                    placeholder={PLACEHOLDERS.CABIN_CLASS}
                    isLabelHidden
                  />
                </div>
              )}
            </div>

            {/* Multi-destination flights rendering */}
            {flightType === FLIGHT_TYPE_CODE[MULTI_DESTINATION] ? (
              <>
                {flights.map((flight, index) => (
                  <React.Fragment key={flight.id}>
                    <div className="col-span-full flex items-center gap-4">
                      <span className="font-bold text-base lg:text-sm">
                        Flight {index + 1}
                      </span>
                      {index > 0 && (
                        <Button
                          type="tertiary"
                          size="sm"
                          buttonType="button"
                          className="**:text-xs **:font-medium min-h-auto"
                          onClick={() => {
                            setFlights(removeFlightFromList(flights, flight.id))
                          }}
                          aria-label={`Remove flight ${index + 1}`}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    {/* Origin Input */}
                    <InputText
                      label="Origin"
                      isLabelHidden
                      iconLeft="search"
                      placeholder={PLACEHOLDERS.ORIGIN}
                      value={flight.origin}
                      onChange={e =>
                        handleUpdateFlight(flight.id, 'origin', e.target.value)
                      }
                    />

                    {/* Destination Input */}
                    <InputText
                      label="Destination"
                      isLabelHidden
                      iconLeft="search"
                      placeholder={PLACEHOLDERS.DESTINATION}
                      value={flight.destination}
                      onChange={e =>
                        handleUpdateFlight(
                          flight.id,
                          'destination',
                          e.target.value
                        )
                      }
                    />

                    {/* Departure Date */}
                    <div className="**:translate-y-0">
                      <DatePicker
                        className="flight-dates"
                        title="Flights: Departure"
                        label="Departing"
                        mode="single"
                        numberOfMonths={1}
                        handleCalendarChange={dates => {
                          if (dates instanceof Date) {
                            handleUpdateFlight(
                              flight.id,
                              'departureDate',
                              dates
                            )
                          }
                          onCalendarChange(dates)
                        }}
                      />
                    </div>
                  </React.Fragment>
                ))}

                {/* Add another flight button */}
                <div className="col-span-full lg:[grid-area:unset]">
                  <Button
                    type="secondary"
                    size="sm"
                    buttonType="button"
                    onClick={() => {
                      setFlights(addFlightToList(flights))
                    }}
                    disabled={!canAddMoreFlights(flights)}
                  >
                    Add Another Flight
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Single/Round-trip flight inputs */}
                <InputText
                  className={getInputClass('origin')}
                  label="Origin"
                  isLabelHidden
                  iconLeft="search"
                  placeholder={PLACEHOLDERS.ORIGIN}
                />

                <InputText
                  className={getInputClass('destination')}
                  label="Destination"
                  isLabelHidden
                  iconLeft="search"
                  placeholder={PLACEHOLDERS.DESTINATION}
                />

                <div className={getInputClass('timeline', flightType)}>
                  <DatePicker
                    className="flight-dates"
                    title={
                      flightType === FLIGHT_TYPE_CODE['round-trip']
                        ? 'Flights: Departing – Returning'
                        : 'Flights: Departing'
                    }
                    label={
                      flightType === FLIGHT_TYPE_CODE['round-trip']
                        ? 'Departing – Returning'
                        : 'Departing'
                    }
                    mode={
                      flightType === FLIGHT_TYPE_CODE['round-trip']
                        ? 'range'
                        : 'single'
                    }
                    numberOfMonths={
                      flightType === FLIGHT_TYPE_CODE['round-trip'] ? 2 : 1
                    }
                    handleCalendarChange={onCalendarChange}
                  />
                </div>
              </>
            )}

            {/* Room with Passengers /Passengers only Input (when hotel package is added) */}
            {addHotel ? (
              <InputButton
                className={getInputClass('passengers', flightType)}
                label={LABELS.PASSENGERS_WITH_HOTEL}
                isLabelHidden
                iconLeft="person"
                placeholder={PLACEHOLDERS.PASSENGERS_WITH_HOTEL}
              />
            ) : (
              <InputButton
                className={getInputClass('passengers', flightType)}
                label={LABELS.PASSENGERS}
                iconLeft="person"
                type="button"
                isLabelHidden
                placeholder={PLACEHOLDERS.PASSENGERS}
              />
            )}

            {/* Cabin Class - conditionally rendered */}
            {!isPackage && (
              <Select
                label={LABELS.CABIN_CLASS}
                isInset
                iconLeft="airline_seat_legroom_extra"
                menuItems={[...CABIN_OPTIONS]}
                initialValue={cabinClass}
                isLabelHidden
                placeholder={PLACEHOLDERS.CABIN_CLASS}
                className={getInputClass('cabin', flightType)}
              />
            )}

            {/* Package Deals */}
            {flightType === FLIGHT_TYPE_CODE['round-trip'] && (
              <div className="flex flex-row items-center justify-start gap-2 text-sm text-nowrap lg:[grid-area:auto/1/auto/-1] lg:order-last [grid-area:unset] sm:[grid-area:deals]">
                <span>
                  <Discount m={1} mr={2} color="success" />
                  {LABELS.PACKAGE_DEALS}
                </span>
                <Label htmlFor="addHotel">
                  <Checkbox
                    name="addHotel"
                    id="addHotel"
                    value="addHotel"
                    checked={addHotel}
                    handleChange={createToggleHandler(addHotel, setAddHotel)}
                  />
                  <span className="ml-2">{LABELS.ADD_HOTEL}</span>
                </Label>
                <Label htmlFor="addCar">
                  <Checkbox
                    name="addCar"
                    id="addCar"
                    value="addCar"
                    checked={addCar}
                    handleChange={createToggleHandler(addCar, setAddCar)}
                  />
                  <span className="ml-2">{LABELS.ADD_CAR}</span>
                </Label>
              </div>
            )}

            {/* Search Button */}
            <Button
              type="primary"
              size="lg"
              className={cn(
                '[grid-area:unset] lg:[grid-area:unset]',
                flightType === FLIGHT_TYPE_CODE[MULTI_DESTINATION]
                  ? 'sm:[grid-area:unset]'
                  : 'sm:[grid-area:submit]',
                flightType === FLIGHT_TYPE_CODE['one-way'] && 'sm:col-span-full'
              )}
            >
              {PLACEHOLDERS.SEARCH_BUTTON}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
